import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, Search, LayoutGrid, List as ListIcon, X, AlertCircle, XCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { api, formatDateBR, ROLES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const PROVIDENCIA_FILTERS = [
  { value: "all", label: "Todas providências" },
  { value: "em_dia", label: "Em Dia" },
  { value: "em_atraso", label: "Em Atraso" },
  { value: "aguardando", label: "Em Providência" },
];

// M14 (2026-06-17): rótulos amigáveis dos filtros do painel executivo
const STATUS_FILTER_LABELS = {
  active: { label: "Contratos ativos", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  pending_assignment: { label: "Aguardando designação", cls: "bg-amber-50 text-amber-900 border-amber-200" },
  pending_director_approval: { label: "Aguardando Diretora", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  pending_director: { label: "Aprovações pendentes", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  overdue: { label: "Em atraso", cls: "bg-rose-50 text-rose-800 border-rose-200" },
  at_risk: { label: "Em risco (< 30 dias)", cls: "bg-red-50 text-red-800 border-red-200" },
  attention: { label: "Em atenção (30-60 dias)", cls: "bg-amber-50 text-amber-900 border-amber-200" },
  completed: { label: "Concluídos", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  cancelled: { label: "Cancelados", cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

const CLOSE_REASONS = [
  { value: "concluido", label: "Contrato concluído" },
  { value: "decurso_prazo", label: "Encerrado por decurso de prazo" },
  { value: "cancelado", label: "Cancelado" },
  { value: "substituido", label: "Substituído por outro contrato" },
  { value: "outro", label: "Outro motivo" },
];

function providenciaLabel(status) {
  switch (status) {
    case "em_dia":
    case "concluida":
      return { label: "Em Dia", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" };
    case "em_atraso":
      return { label: "Em Atraso", cls: "bg-red-50 text-red-800 border-red-200" };
    case "aguardando":
      return { label: "Em Providência", cls: "bg-amber-50 text-amber-900 border-amber-200" };
    default:
      return { label: "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  }
}

export default function ContractsList() {
  const { user } = useAuth();
  const isSecretaria = user.role === ROLES.SECRETARIA;
  const isConsultor = user.role === ROLES.CONSULTOR;
  const isJuridico = user.role === ROLES.JURIDICO;
  const isDiretora = user.role === ROLES.DIRETORA;

  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const [contracts, setContracts] = useState(null);
  const [search, setSearch] = useState("");
  const [providenciaFilter, setProvidenciaFilter] = useState(searchParams.get("providencia") || "all");
  const [groupByClient, setGroupByClient] = useState(false);

  // M13: modal de baixa (só Diretora)
  const [closingContract, setClosingContract] = useState(null);
  const [closeReason, setCloseReason] = useState("concluido");
  const [closeNotes, setCloseNotes] = useState("");
  const [closing, setClosing] = useState(false);

  const basePath = isConsultor
    ? "/consultor"
    : isJuridico
      ? "/juridico"
      : isDiretora
        ? "/diretora"
        : "/secretaria";

  const loadContracts = () => {
    const params = statusFilter ? { status_filter: statusFilter } : undefined;
    api.get("/contracts", { params }).then(({ data }) => setContracts(data));
  };

  useEffect(() => {
    loadContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    // Preserva ambos os filtros na URL
    const newParams = {};
    if (providenciaFilter && providenciaFilter !== "all") newParams.providencia = providenciaFilter;
    if (statusFilter) newParams.status = statusFilter;
    setSearchParams(newParams);
  }, [providenciaFilter, statusFilter, setSearchParams]);

  const clearStatusFilter = () => {
    const newParams = {};
    if (providenciaFilter && providenciaFilter !== "all") newParams.providencia = providenciaFilter;
    setSearchParams(newParams);
  };

  // M13: baixar contrato
  const submitClose = async () => {
    if (!closingContract) return;
    setClosing(true);
    try {
      await api.post(`/contracts/${closingContract.id}/close`, {
        reason: closeReason,
        notes: closeNotes || null,
      });
      toast.success("Contrato encerrado com sucesso.");
      setClosingContract(null);
      setCloseReason("concluido");
      setCloseNotes("");
      loadContracts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao encerrar contrato.");
    } finally {
      setClosing(false);
    }
  };

  const filtered = useMemo(() => {
    if (!contracts) return [];
    let list = contracts;
    const q = search.toLowerCase().trim();
    if (q) {
      list = list.filter((c) =>
        c.client_name?.toLowerCase().includes(q) ||
        c.product_name?.toLowerCase().includes(q) ||
        c.consultant_name?.toLowerCase().includes(q),
      );
    }
    if (isSecretaria && providenciaFilter !== "all") {
      list = list.filter((c) => {
        if (providenciaFilter === "em_dia") return c.providencia_status === "em_dia" || c.providencia_status === "concluida";
        return c.providencia_status === providenciaFilter;
      });
    }
    return list;
  }, [contracts, search, providenciaFilter, isSecretaria]);

  const grouped = useMemo(() => {
    if (!groupByClient) return null;
    const map = new Map();
    for (const c of filtered) {
      const key = c.client_name || "—";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b, "pt-BR"));
  }, [filtered, groupByClient]);

  if (!contracts) return <Skeleton className="h-96" />;

  const Row = ({ c }) => (
    <tr
      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
      onClick={(e) => {
        // não navega quando clicar no botão de baixa
        if (e.target.closest("[data-close-btn]")) return;
        window.location.href = `${basePath}/contratos/${c.id}`;
      }}
      data-testid={`contracts-row-${c.id}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-slate-400" />
          <span className="font-medium text-slate-900">{c.client_name}</span>
          {c.is_overdue && (
            <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-[10px]">
              {c.days_overdue}d atraso
            </Badge>
          )}
          {c.closed_at && (
            <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-[10px]">
              Encerrado
            </Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-slate-700">{c.product_name}</td>
      <td className="px-4 py-3 text-slate-700">
        {c.consultant_name || (
          <span className="text-slate-400 italic text-xs">A designar (Diretora)</span>
        )}
      </td>
      {isSecretaria ? (
        <td className="px-4 py-3">
          {(() => { const p = providenciaLabel(c.providencia_status); return (
            <Badge variant="outline" className={`${p.cls} border text-xs`}>{p.label}</Badge>
          ); })()}
        </td>
      ) : (
        <>
          <td className="px-4 py-3">
            <Badge variant="outline" className="border-slate-200 text-xs">{c.status?.replace(/_/g, " ")}</Badge>
          </td>
          <td className="px-4 py-3 w-48">
            <div className="flex items-center gap-2">
              <Progress value={c.progress_pct} className="h-1.5 flex-1" />
              <span className="text-xs text-slate-600 w-9 text-right">{c.progress_pct}%</span>
            </div>
          </td>
        </>
      )}
      <td className="px-4 py-3 text-slate-700">{formatDateBR(c.end_date)}</td>
      {isDiretora && (
        <td className="px-4 py-3 text-right">
          {c.closed_at ? (
            <span className="text-xs text-slate-500 italic">
              Baixa: {formatDateBR(c.closed_at)}
            </span>
          ) : (
            <Button
              variant="outline"
              size="sm"
              data-close-btn
              className="text-rose-700 border-rose-200 hover:bg-rose-50"
              onClick={(e) => { e.stopPropagation(); setClosingContract(c); }}
              data-testid={`contract-close-btn-${c.id}`}
            >
              <XCircle className="w-3.5 h-3.5 mr-1" /> Dar baixa
            </Button>
          )}
        </td>
      )}
    </tr>
  );

  const TableHeader = () => (
    <thead>
      <tr className="bg-slate-50 border-b border-slate-200 text-left">
        <th className="px-4 py-3 font-medium text-slate-700">Cliente</th>
        <th className="px-4 py-3 font-medium text-slate-700">Produto</th>
        <th className="px-4 py-3 font-medium text-slate-700">Consultor</th>
        {isSecretaria ? (
          <th className="px-4 py-3 font-medium text-slate-700">Providência</th>
        ) : (
          <>
            <th className="px-4 py-3 font-medium text-slate-700">Status</th>
            <th className="px-4 py-3 font-medium text-slate-700">Avanço</th>
          </>
        )}
        <th className="px-4 py-3 font-medium text-slate-700">Vence em</th>
        {isDiretora && (
          <th className="px-4 py-3 font-medium text-slate-700 text-right">Ações</th>
        )}
      </tr>
    </thead>
  );

  return (
    <div className="space-y-6" data-testid="contracts-list-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Contratos</h1>
        <p className="text-sm text-slate-600 mt-1">
          Lista completa de contratos. Clique para abrir detalhes.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por cliente, produto ou consultor…"
            className="pl-9"
            data-testid="contracts-search"
          />
        </div>
        {isSecretaria && (
          <Select value={providenciaFilter} onValueChange={setProvidenciaFilter}>
            <SelectTrigger className="w-[200px]" data-testid="providencia-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROVIDENCIA_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button
          variant={groupByClient ? "default" : "outline"}
          size="sm"
          onClick={() => setGroupByClient((v) => !v)}
          className={groupByClient ? "bg-midnight hover:bg-ink text-white" : ""}
          data-testid="toggle-group-by-client"
        >
          {groupByClient ? <LayoutGrid className="w-4 h-4 mr-1" /> : <ListIcon className="w-4 h-4 mr-1" />}
          {groupByClient ? "Agrupado por cliente" : "Organizar por cliente"}
        </Button>
      </div>

      {/* M14: badge de filtro ativo do painel executivo */}
      {statusFilter && STATUS_FILTER_LABELS[statusFilter] && (
        <div
          className="flex items-center gap-2 p-3 rounded-md border border-slate-200 bg-slate-50"
          data-testid="active-filter-banner"
        >
          <AlertCircle className="w-4 h-4 text-slate-600" />
          <span className="text-sm text-slate-700">
            Filtro ativo:{" "}
            <Badge variant="outline" className={`${STATUS_FILTER_LABELS[statusFilter].cls} border text-xs ml-1`}>
              {STATUS_FILTER_LABELS[statusFilter].label}
            </Badge>
          </span>
          <span className="text-xs text-slate-500 ml-auto">
            {contracts?.length ?? 0} contrato{contracts?.length === 1 ? "" : "s"}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-600 hover:text-slate-900"
            onClick={clearStatusFilter}
            data-testid="clear-status-filter"
          >
            <X className="w-3.5 h-3.5 mr-1" /> Limpar filtro
          </Button>
        </div>
      )}

      {grouped ? (
        <div className="space-y-6">
          {grouped.map(([clientName, items]) => (
            <Card key={clientName} className="card-soft overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-midnight" />
                  <span className="font-semibold text-slate-900">{clientName}</span>
                </div>
                <span className="text-xs text-slate-500">{items.length} contrato{items.length === 1 ? "" : "s"}</span>
              </div>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <TableHeader />
                  <tbody>
                    {items.map((c) => <Row key={c.id} c={c} />)}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ))}
          {grouped.length === 0 && (
            <Card className="card-soft"><CardContent className="py-8 text-center text-sm text-slate-500">Nenhum contrato encontrado.</CardContent></Card>
          )}
        </div>
      ) : (
        <Card className="card-soft overflow-hidden">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <TableHeader />
              <tbody>
                {filtered.map((c) => <Row key={c.id} c={c} />)}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={isDiretora ? 7 : 6} className="px-4 py-6 text-center text-slate-500">
                      Nenhum contrato encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* M13: Modal para baixa manual (Diretora) */}
      <Dialog open={!!closingContract} onOpenChange={(v) => !v && setClosingContract(null)}>
        <DialogContent className="max-w-lg" data-testid="close-contract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-rose-700" />
              Dar baixa no contrato
            </DialogTitle>
            <DialogDescription>
              Você está encerrando administrativamente o contrato de{" "}
              <strong>{closingContract?.client_name}</strong>.
              Esta ação <strong>não apaga</strong> reuniões, documentos, vídeos ou histórico —
              apenas registra o encerramento e atualiza os indicadores do painel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Motivo da baixa</Label>
              <Select value={closeReason} onValueChange={setCloseReason}>
                <SelectTrigger className="mt-1" data-testid="close-reason-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLOSE_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Observação interna (opcional)</Label>
              <Textarea
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                rows={3}
                className="mt-1"
                placeholder="Ex.: contrato substituído pelo processo n° X…"
                data-testid="close-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosingContract(null)} disabled={closing}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={submitClose}
              disabled={closing}
              data-testid="close-contract-confirm"
            >
              {closing ? "Encerrando..." : "Confirmar baixa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
